import type React from 'react';
import {
  Td,
  Tr,
  Chip,
  TextLink,
  colors,
  designTokens,
  Flex,
  Box,
  RiFilePaperLine,
  RiPencilLine,
  RiSquareFill,
  Label
} from '@itandi/itandi-bb-ui';
import type { DealIndexVendor } from '~/models/Deal';
import { DealTypeChip } from '~/components/common/parts/chip/DealTypeChip';
import { truncate } from '~/utils/StringUtil';
import { PATH as VENDOR_DEAL_PATH } from '~/pages/vendors/deals/[id]';
import { statusColor } from '~/models/Status';

type Props = {
  deal: DealIndexVendor;
  statusNames: { [p: string]: string };
};

export const DealTableSPRow: React.FC<Props> = ({
  deal,
  statusNames
}: Props) => {
  const unread = deal.unreadMessageCount > 0 || deal.displayUnread;
  const unreadChipLabel =
    deal.unreadMessageCount > 0 ? `未読${deal.unreadMessageCount}` : '未読';
  return (
    <Tr>
      <Td textAlign="Left" verticalAlign="Top">
        <Box marginY={2}>
          <Chip
            Icon={RiSquareFill}
            iconColor={statusColor[deal.status]}
            label={statusNames[deal.status]}
          />
        </Box>
        {unread && (
          <Box marginY={2}>
            <Chip
              background={colors['semantic-negative']}
              borderColor={null}
              label={unreadChipLabel}
              textColor={colors['itandi-white']}
            />
          </Box>
        )}
        {deal.hasTemporaryDealCancel && (
          <Box marginY={2}>
            <Chip
              background={colors['semantic-negative']}
              borderColor={null}
              Icon={RiFilePaperLine}
              iconColor={colors['itandi-white']}
              label="取下申請あり"
              textColor={colors['itandi-white']}
            />
          </Box>
        )}
        {deal.hasRequestEstimation && (
          <Box marginY={2}>
            <Chip
              background={colors['expressive-blue']}
              borderColor={null}
              Icon={RiFilePaperLine}
              iconColor={colors['itandi-white']}
              label="見積申請中"
              textColor={colors['itandi-white']}
            />
          </Box>
        )}
        {deal.hasDraftEstimation && (
          <Box marginY={2}>
            <Chip
              background={colors['expressive-yellow-dark']}
              borderColor={null}
              Icon={RiPencilLine}
              iconColor={colors['itandi-white']}
              label="見積編集中"
              textColor={colors['itandi-white']}
            />
          </Box>
        )}
        {deal.hasRequestEstimationByCompany && (
          <Box marginY={2}>
            <Chip
              background={colors['expressive-aqua-dark']}
              borderColor={null}
              Icon={RiFilePaperLine}
              iconColor={colors['itandi-white']}
              label="見積申請あり"
              textColor={colors['itandi-white']}
            />
          </Box>
        )}
      </Td>
      <Td textAlign="Left" verticalAlign="Top">
        <Box>
          <Flex gap={2}>
            <DealTypeChip
              dealSubtype={deal.dealSubtype}
              dealType={deal.dealType}
            />
          </Flex>
        </Box>
        <Box>
          <TextLink
            href={VENDOR_DEAL_PATH(deal.id)}
            target="_blank"
            variant="PrimaryBold"
          >
            {`${truncate(deal.name, 30)} ${deal.roomNumber || ''}`}
          </TextLink>
          <Flex direction="column" gap={designTokens['spacing/xxxs']}>
            <Label color="subtle">{truncate(deal.address, 35)}</Label>
            {deal.tenantName && (
              <Label color="subtle">
                {`${truncate(deal.tenantName, 15)} 様`}
              </Label>
            )}
          </Flex>
        </Box>
      </Td>
    </Tr>
  );
};
